import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getAllMissingImplantReports } from '../../services/apiService';
import ConditionalsTable from '../implant/conditionals/ConditionalsTable';
import LoadingRing from '../ui-components/LoadingRing';
import styles from './MissingImplantReports.module.css';

interface MissingImplantReport {
    _id: string;
    searchTerm: string;
    name: string;
    type: string;
    info: string;
    createdAt?: string;
    updatedAt?: string;
    user_id?: {
        name: string;
        email: string;
        department_id?: {
            name: string;
        };
    };
}

const MissingImplantReports: React.FC = () => {
    const { t } = useTranslation();
    const [reports, setReports] = useState<MissingImplantReport[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchReports = async () => {
        try {
            setLoading(true);
            const data = await getAllMissingImplantReports();
            setReports(data);
        } catch (err) {
            setError('Failed to fetch missing implant reports');
            console.error('Error fetching missing implant reports:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReports();
    }, []);

    const formatDate = (dateString?: string) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString();
    };

    const headers = [
        t('missingImplantReports.table.headers.searchTerm'),
        t('missingImplantReports.table.headers.name'),
        t('missingImplantReports.table.headers.type'),
        t('missingImplantReports.table.headers.info'),
        t('missingImplantReports.table.headers.reportedBy'),
        t('missingImplantReports.table.headers.department'),
        t('missingImplantReports.table.headers.reportedAt'),
    ];

    const fields = reports.map((report) => [
        report.searchTerm,
        report.name,
        report.type,
        report.info,
        report.user_id?.name || '-',
        report.user_id?.department_id?.name || '-',
        formatDate(report.createdAt),
    ]);

    if (loading) {
        return (
            <div className={styles.wrapper}>
                <div className={styles.header}>
                    <h1>{t('missingImplantReports.title')}</h1>
                </div>
                <div className={styles.loading}>
                    <LoadingRing size="large" />
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.wrapper}>
                <div className={styles.header}>
                    <h1>{t('missingImplantReports.title')}</h1>
                </div>
                <div className={styles.error}>
                    <p>{error}</p>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.wrapper}>
            <div className={styles.header}>
                <h1>{t('missingImplantReports.title')}</h1>
                <p className={styles.subtitle}>
                    {t('missingImplantReports.subtitle', {
                        count: reports.length,
                    })}
                </p>
            </div>

            {reports.length === 0 ? (
                <div className={styles.emptyState}>
                    <h3>{t('missingImplantReports.emptyState.title')}</h3>
                    <p>{t('missingImplantReports.emptyState.message')}</p>
                </div>
            ) : (
                <div className={styles.tableWrapper}>
                    <ConditionalsTable
                        headers={headers}
                        fields={fields}
                        style="default"
                        hasSeeMore={true}
                        sizes={[2, 2, 2, 3, 2, 2, 2]} // Relative widths for columns
                    />
                </div>
            )}
        </div>
    );
};

export default MissingImplantReports;
